import { Trash2, Map as MapIcon, Timer, Hand } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ActionType, RouteWithAction } from '@/types'
import {
  formatMapId,
  getSlotLabel,
  isResumeWaitStep,
  isWaitStep,
} from '@/common/routeUtil'
import FloatingPanel from './FloatingPanel'
import WaitSecondsInput from './WaitSecondsInput'

interface UsedRoutesPanelProps {
  routes: RouteWithAction[]
  thumbnails: Map<string, string>
  pos: { x: number; y: number }
  onMove: (pos: { x: number; y: number }) => void
  // 未指定なら × を表示しない
  onClose?: () => void
  height: number
  onResize: (height: number) => void
  // 編集モードのみ: 行選択・削除の操作。参照モードでは省略（読み取り専用）。
  selectedIndex?: number | null
  onSelect?: (index: number) => void
  onDelete?: (index: number) => void
  // 編集モードのみ: 停止手順の秒数変更。参照モードでは省略（読み取り専用表示）。
  onWaitChange?: (index: number, sec: number) => void
  // 前のルートと繋がっていない（距離エラー）行のインデックス集合。日付を赤表示する。
  errorIndices?: Set<number>
  // 選択行の配色（既定はオレンジ。参照モードはマップと揃えて青）。
  selectedColor?: 'orange' | 'blue'
}

// シナリオが使用するルートの一覧を表示するフローティングパネル。
// 参照モードでは選択中シナリオの構成、編集モードでは編集中のカートを表示する。
export default function UsedRoutesPanel({
  routes,
  thumbnails,
  pos,
  onMove,
  onClose,
  height,
  onResize,
  selectedIndex,
  onSelect,
  onDelete,
  onWaitChange,
  selectedColor = 'orange',
  errorIndices,
}: UsedRoutesPanelProps) {
  const { t } = useTranslation()

  const selectedClass =
    selectedColor === 'blue'
      ? 'bg-blue-100 border-blue-500'
      : 'bg-orange-100 border-orange-500'

  const actionLabel = (action?: ActionType) =>
    action === 'pickup'
      ? t('scenarios.pickup')
      : action === 'place'
        ? t('scenarios.place')
        : action === 'move_loaded'
          ? t('scenarios.moveLoaded')
          : t('scenarios.move')

  return (
    <FloatingPanel
      title={t('scenarios.usedRoutesTitle')}
      pos={pos}
      onMove={onMove}
      onClose={onClose}
      width="w-80"
      height={height}
      onResize={onResize}
    >
      <div className="p-2 h-full overflow-y-auto">
        {routes.length === 0 ? (
          <div className="text-gray-400 text-center py-6 text-xs">
            {t('scenarios.noRoutes')}
          </div>
        ) : (
          routes.map((route, index) => {
            const selected = selectedIndex === index
            const disconnected = errorIndices?.has(index) ?? false

            // 停止手順: サムネイルの代わりに時計アイコン。編集モードでは秒数を行内で変更できる
            // （先頭の停止手順はスロットを持たないため、ラベル計算より前に分岐する）。
            // 一時停止手順（タップで再開）は手のアイコンで秒数入力なし
            if (isWaitStep(route)) {
              const isResume = isResumeWaitStep(route)
              return (
                <div
                  key={`${route.id}-${index}`}
                  onClick={onSelect ? () => onSelect(index) : undefined}
                  className={`flex items-center gap-2 p-1.5 mb-1.5 border rounded-lg transition-colors ${
                    onSelect ? 'cursor-pointer' : ''
                  } ${
                    selected
                      ? selectedClass
                      : 'bg-gray-50 hover:bg-gray-100 border-transparent'
                  }`}
                >
                  <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center bg-white text-blue-400 border border-blue-400 rounded-full font-bold text-xs">
                    {index + 1}
                  </div>
                  <div className="h-9 w-11 flex-shrink-0 border border-gray-300 rounded bg-purple-50 flex items-center justify-center">
                    {isResume ? (
                      <Hand size={16} className="text-purple-500" />
                    ) : (
                      <Timer size={16} className="text-purple-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden text-xs">
                    <div className="font-medium text-gray-700">
                      {isResume
                        ? t('scenarios.waitResumeStep')
                        : t('scenarios.waitStep')}
                    </div>
                    {isResume ? (
                      <div className="text-[10px] text-purple-700">
                        {t('scenarios.waitResumeBadge')}
                      </div>
                    ) : onWaitChange ? (
                      <div
                        className="mt-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <WaitSecondsInput
                          compact
                          value={route.waitSec ?? 0}
                          onChange={(sec) => onWaitChange(index, sec)}
                        />
                      </div>
                    ) : (
                      <div className="text-[10px] text-purple-700">
                        {t('scenarios.waitBadge', { sec: route.waitSec ?? 0 })}
                      </div>
                    )}
                  </div>
                  {onDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onDelete(index)
                      }}
                      className="text-gray-400 hover:text-red-500 p-1"
                      aria-label="delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              )
            }

            const formatted = formatMapId(route.mapId)
            const [datePart, timePart] = formatted.split(' ')
            const startLabel = getSlotLabel(route.startSlot!)
            const endLabel = getSlotLabel(route.endSlot!)

            return (
              <div
                key={`${route.id}-${index}`}
                onClick={onSelect ? () => onSelect(index) : undefined}
                className={`flex items-center gap-2 p-1.5 mb-1.5 border rounded-lg transition-colors ${
                  onSelect ? 'cursor-pointer' : ''
                } ${
                  selected
                    ? selectedClass
                    : 'bg-gray-50 hover:bg-gray-100 border-transparent'
                }`}
              >
                <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center bg-white text-blue-400 border border-blue-400 rounded-full font-bold text-xs">
                  {index + 1}
                </div>
                <div className="h-9 w-11 flex-shrink-0 border border-gray-300 rounded bg-gray-100 overflow-hidden flex items-center justify-center">
                  {thumbnails.get(route.slots[0]) ? (
                    <img
                      src={thumbnails.get(route.slots[0])}
                      alt={`${index + 1}`}
                      className="w-full h-full object-contain"
                      draggable={false}
                    />
                  ) : (
                    <MapIcon size={14} className="text-gray-300" />
                  )}
                </div>
                {/* nowrap のバッジがゴミ箱側へはみ出さないよう overflow-hidden で仕切る */}
                <div className="flex-1 min-w-0 overflow-hidden text-xs">
                  <div
                    className={
                      disconnected ? 'font-bold text-red-600' : 'text-gray-500'
                    }
                  >
                    {datePart} {timePart}
                  </div>
                  <div className="flex gap-1 items-center">
                    <span className="font-medium text-gray-700 whitespace-nowrap">
                      {startLabel} → {endLabel}
                    </span>
                    <span className="text-[10px] border border-gray-400 px-1 rounded whitespace-nowrap">
                      {actionLabel(route.action)}
                    </span>
                  </div>
                </div>
                {onDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete(index)
                    }}
                    className="text-gray-400 hover:text-red-500 p-1"
                    aria-label="delete"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            )
          })
        )}
      </div>
    </FloatingPanel>
  )
}
